import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { loadRemoteModule } from '@nx/angular/mf';
import { logger } from '@mpt-fo/common/telemetry';
import { EventBusService } from '@mpt-fo/common/core/services/event-bus.service';
import type { EventDetail } from '@mwp/event-bus';
import { MicroFrontendService } from '@mpt-fo/common/core/services/micro-frontend.service';

interface NavigationEventData {
    path: string;
}

@Component({
    selector: 'app-react-mfe-wrapper',
    templateUrl: './react-mfe-wrapper.component.html',
    styleUrls: ['./react-mfe-wrapper.component.scss'],
})
export class ReactMfeWrapperComponent implements OnInit, OnDestroy {
    @Input() mfeName: string;
    @Input() moduleName: string;
    @Input() entryPoint: string;
    @Input() useAppBaseFontSize: boolean;
    @Input() reactProps: Record<any, any>;
    DEFAULT_MODULE = './Module';
    loading = true;
    error: any;
    lazyLoadComponent: any;
    isApp = false;
    props = {
        eventBus: this.eventBusService.getInstance(),
    };

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private eventBusService: EventBusService,
        private microFrontendService: MicroFrontendService,
    ) {}

    async importMfe(mfeName: string, entryPoint: string) {
        this.loading = true;
        this.error = null;
        try {
            const mfeModule = await this.microFrontendService.getRemoteModule(mfeName, entryPoint);
            this.isApp = this.shouldTreatAsApp(mfeModule, entryPoint);
            if (this.isApp) {
                this.setAppSyles();
                // workaround to reset react router state
                mfeModule.router?.navigate(this.router.url);
            }

            if (this.useAppBaseFontSize) {
                // workaround to keep consistent font size and spacing
                this.setBaseFontSize();
            }
            return this.getModuleExport(mfeModule, entryPoint);
        } catch (e) {
            this.error = e;
            logger.error('MFE Error', mfeName, e);
        } finally {
            this.loading = false;
        }
    }

    private shouldTreatAsApp(mfeModule: any, entryPoint: string): boolean {
        const isModuleUnavailable = !this.moduleName || !mfeModule[this.moduleName];
        return isModuleUnavailable && entryPoint === this.DEFAULT_MODULE;
    }

    private getModuleExport(mfeModule: any, entryPoint: string) {
        if (entryPoint !== this.DEFAULT_MODULE || this.isApp) {
            return mfeModule.default ?? mfeModule;
        }

        return mfeModule[this.moduleName];
    }

    handleHostNavigation = (eventData: EventDetail<NavigationEventData>) => {
        this.router.navigateByUrl(eventData.data.path);
    };

    triggerRemoteNavigation = (path: string) => {
        this.eventBusService.getInstance().publish<NavigationEventData>(`${this.mfeName}:navigate`, { data: { path } });
    };

    ngOnInit() {
        this.eventBusService.getInstance().subscribe<NavigationEventData>('host:navigate', this.handleHostNavigation);
        this.mfeName = this.activatedRoute.snapshot.data?.mfeName ?? this.mfeName;
        this.entryPoint = this.entryPoint ?? this.DEFAULT_MODULE;
        this.lazyLoadComponent = () => this.importMfe(this.mfeName, this.entryPoint);
        this.activatedRoute.url.subscribe(() => {
            this.triggerRemoteNavigation(this.router.url);
        });
        if (this.reactProps) {
            this.props = {
                ...this.props,
                ...this.reactProps,
            };
        }
    }

    ngOnDestroy() {
        if (this.isApp || this.useAppBaseFontSize) {
            this.unsetAppStyles();
        }
        this.eventBusService.getInstance().unsubscribe<NavigationEventData>('host:navigate', this.handleHostNavigation);
    }

    setBaseFontSize() {
        // set base size on html to get correct rem size in MFE
        document.documentElement.classList.add('is-react-mfe-active');
    }

    setAppSyles() {
        this.setBaseFontSize();
        // cancel out content styles of host
        document.getElementsByClassName('inner-content main-background')?.[0]?.classList.add('is-react-app');
    }

    unsetAppStyles() {
        document.documentElement.classList?.remove('is-react-mfe-active');
        document.getElementsByClassName('inner-content main-background')?.[0]?.classList?.remove('is-react-app');
    }
}
